import React from 'react'
import { Link } from 'react-router-dom'

/**
 * Public privacy policy page (no auth). Referenced from the OAuth consent screen
 * URL field, so it MUST be publicly reachable at the apex domain (marzz.ai/privacy)
 * before submitting the Google Business Profile API access request form.
 *
 * Template only — review with legal counsel before relying on it in production.
 */
const EFFECTIVE_DATE = 'June 6, 2026'

const PrivacyPolicy: React.FC = () => (
  <div className="max-w-3xl mx-auto px-4 py-12 text-gray-800 leading-relaxed">
    <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
    <p className="text-sm text-gray-500 mb-8">Effective date: {EFFECTIVE_DATE}</p>

    <Section title="Who we are">
      <p>
        Marzz AI (&ldquo;Marzz&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) operates a Local SEO
        marketing platform that helps agencies and businesses manage their Google Business Profile
        presence, run keyword research, monitor search rankings, and coordinate client
        communications. We are an entity of ExploringNotBoring. You can reach us at{' '}
        <a className="text-primary-700 hover:underline" href="mailto:privacy@marzz.ai">
          privacy@marzz.ai
        </a>
        .
      </p>
    </Section>

    <Section title="Information we collect">
      <p className="mb-3">We collect only the information needed to provide the service:</p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Account information</strong> you provide when signing up: name, email address,
          hashed password, and the company or agency you belong to.
        </li>
        <li>
          <strong>Google Business Profile (GBP) data</strong> you authorize us to access via Google
          OAuth. This includes the business locations, listing details, and (when you enable later
          features) Performance metrics, Q&amp;A, and reviews you manage on Google. The data is read
          from Google&rsquo;s Business Profile APIs only with the scopes you grant.
        </li>
        <li>
          <strong>OAuth credentials</strong> (access and refresh tokens) used to call Google APIs on
          your behalf. These are stored in our database, transmitted only over encrypted (TLS)
          connections, masked in any administrative views, and never exposed to other customers.
        </li>
        <li>
          <strong>CRM data</strong> you choose to sync, such as contacts, calls, and opportunities
          from third-party CRMs you connect (for example, GoHighLevel).
        </li>
        <li>
          <strong>Usage and diagnostic data</strong> generated when you use the app: page views,
          feature interactions, server logs, and error reports. We use this to keep the service
          working and to improve it.
        </li>
      </ul>
    </Section>

    <Section title="How we use your information">
      <ul className="list-disc pl-6 space-y-2">
        <li>To provide, maintain, and operate the platform (dashboards, reports, sync jobs).</li>
        <li>To authenticate you and protect your account.</li>
        <li>To send transactional emails (invitations, report links, security notifications).</li>
        <li>To improve and debug the product (aggregated analytics and error monitoring).</li>
        <li>To comply with legal obligations.</li>
      </ul>
      <p className="mt-3">
        We do <strong>not</strong> use your data to train generalized AI or machine learning models,
        and we do <strong>not</strong> sell or rent your data to anyone.
      </p>
    </Section>

    <Section title="Google API Services User Data Policy (Limited Use)">
      <p className="mb-3">
        Marzz&rsquo;s use and transfer of information received from Google APIs to any other app
        will adhere to the{' '}
        <a
          className="text-primary-700 hover:underline"
          href="https://developers.google.com/terms/api-services-user-data-policy"
          target="_blank"
          rel="noreferrer"
        >
          Google API Services User Data Policy
        </a>
        , including the Limited Use requirements. Specifically:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          Google user data accessed through OAuth is used <strong>only</strong> to deliver and
          improve the user-facing features of Marzz (e.g. displaying your GBP locations in the
          dashboard, generating reports, syncing business info).
        </li>
        <li>
          We do <strong>not</strong> transfer Google user data to third parties except as necessary
          to provide or improve the user-facing features (for example, to our cloud hosting
          provider), to comply with applicable law, or as part of a merger, acquisition, or sale of
          assets &mdash; and in all such cases, only under appropriate confidentiality and data
          protection terms.
        </li>
        <li>
          We do <strong>not</strong> use Google user data for serving advertisements.
        </li>
        <li>
          We do <strong>not</strong> allow humans to read Google user data unless we have your
          explicit consent, it is required by law, the data is aggregated and de-identified, or it
          is required to investigate abuse or for security or compliance purposes.
        </li>
      </ul>
    </Section>

    <Section title="Scopes we request from Google">
      <p className="mb-3">When you connect Google Business Profile, we request:</p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <code className="bg-gray-100 rounded px-1.5 py-0.5">openid</code> and{' '}
          <code className="bg-gray-100 rounded px-1.5 py-0.5">email</code> &mdash; to identify which
          Google user authorized the connection.
        </li>
        <li>
          <code className="bg-gray-100 rounded px-1.5 py-0.5">
            https://www.googleapis.com/auth/business.manage
          </code>{' '}
          &mdash; to read and (in future features) update the Business Profile listings you select.
        </li>
      </ul>
      <p className="mt-3">
        You can review and revoke these grants at any time at{' '}
        <a
          className="text-primary-700 hover:underline"
          href="https://myaccount.google.com/permissions"
          target="_blank"
          rel="noreferrer"
        >
          myaccount.google.com/permissions
        </a>
        .
      </p>
    </Section>

    <Section title="Service providers">
      <p className="mb-3">
        We use trusted third-party providers to operate Marzz. They process data only on our
        instructions and under written data protection terms:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Amazon Web Services (AWS)</strong> &mdash; cloud hosting and database.
        </li>
        <li>
          <strong>SendGrid (Twilio)</strong> &mdash; transactional email delivery.
        </li>
        <li>
          <strong>Mapbox</strong> &mdash; map rendering for geographic reports.
        </li>
        <li>
          <strong>Stripe</strong> &mdash; subscription billing (when applicable).
        </li>
      </ul>
    </Section>

    <Section title="Data retention and deletion">
      <ul className="list-disc pl-6 space-y-2">
        <li>
          Account data is retained while your account is active. If you delete your account, we
          delete or anonymize personal data within 30 days, except where retention is required by
          law or for legitimate business records (e.g. accounting).
        </li>
        <li>
          When you disconnect Google Business Profile from a company (or delete your account), the
          access and refresh tokens are revoked with Google on a best-effort basis and cleared from
          our database, so no usable Google credential is retained.
        </li>
        <li>Backups containing personal data are encrypted and rotated out within 90 days.</li>
        <li>
          To request deletion or a copy of your data, email{' '}
          <a className="text-primary-700 hover:underline" href="mailto:privacy@marzz.ai">
            privacy@marzz.ai
          </a>
          .
        </li>
      </ul>
    </Section>

    <Section title="Security">
      <p>
        We protect your data in transit with TLS. Access to production systems is restricted to a
        small number of authorized engineers and is logged. OAuth tokens are masked in any
        administrative views, are never sent to the browser, and are cleared when you disconnect. No
        security program is perfect; we work continuously to improve ours and will notify affected
        users of material incidents as required by law.
      </p>
    </Section>

    <Section title="Your rights">
      <p className="mb-3">
        Depending on where you live (e.g. California, the EEA, UK), you may have rights to access,
        correct, delete, restrict, or port your personal information, or to object to certain
        processing. To exercise any of these, contact{' '}
        <a className="text-primary-700 hover:underline" href="mailto:privacy@marzz.ai">
          privacy@marzz.ai
        </a>
        . We respond within the timeframes required by applicable law.
      </p>
    </Section>

    <Section title="Children">
      <p>
        Marzz is a business tool not intended for children under 16. We do not knowingly collect
        personal information from children.
      </p>
    </Section>

    <Section title="International transfers">
      <p>
        Marzz is operated from the United States. If you access the service from outside the US, you
        understand that your information will be transferred to and processed in the US, subject to
        appropriate safeguards.
      </p>
    </Section>

    <Section title="Changes to this policy">
      <p>
        We may update this policy from time to time. When we make material changes, we will revise
        the effective date at the top of this page and, where appropriate, notify you in-product or
        by email.
      </p>
    </Section>

    <Section title="Contact">
      <p>
        Questions or requests? Email{' '}
        <a className="text-primary-700 hover:underline" href="mailto:privacy@marzz.ai">
          privacy@marzz.ai
        </a>
        .
      </p>
    </Section>

    <div className="mt-12 pt-6 border-t border-gray-200 text-sm text-gray-500">
      See also:{' '}
      <Link to="/terms" className="text-primary-700 hover:underline">
        Terms of Service
      </Link>
    </div>
  </div>
)

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl font-semibold text-gray-900 mb-3">{title}</h2>
    {children}
  </section>
)

export default PrivacyPolicy
